import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Volume2,
  VolumeX,
  ArrowLeft,
  SkipForward,
  AlertCircle,
  Maximize,
  Minimize,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = "/api";
const STREAM_URL = "/api/stream";
const RESYNC_INTERVAL = 90000;
const DRIFT_THRESHOLD = 2;

const getAuthToken = () => localStorage.getItem("auth_token");

export default function PlayerPage() {
  const { seriesName } = useParams();
  const navigate = useNavigate();

  const videoRef = useRef(null);
  const nextVideoRef = useRef(null);
  const containerRef = useRef(null);
  const resyncTimerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  const [allSeries, setAllSeries] = useState([]);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [nextPreloaded, setNextPreloaded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const getStreamUrl = useCallback((series, filename) => {
    const token = getAuthToken();
    return `${STREAM_URL}/${series}/${encodeURIComponent(filename)}?token=${token}`;
  }, []);

  useEffect(() => {
    const fetchSeries = async () => {
      try {
        const response = await axios.get(`${API}/series`);
        setAllSeries(response.data);
      } catch (err) {
        console.error("Failed to fetch series list", err);
      }
    };
    fetchSeries();
  }, []);

  const fetchNowPlaying = useCallback(async (isResync = false) => {
    try {
      const response = await axios.get(`${API}/now_playing?series=${seriesName}`);
      const data = response.data;

      if (isResync && videoRef.current && nowPlaying) {
        const current = videoRef.current.currentTime;
        const drift = Math.abs(current - data.offset);

        if (drift > DRIFT_THRESHOLD) {
          videoRef.current.currentTime = data.offset;
        }

        if (data.video !== nowPlaying.video) {
          setNowPlaying(data);
          setNextPreloaded(false);
        }
      } else {
        setNowPlaying(data);
        setNextPreloaded(false);
      }
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load channel");
    } finally {
      setLoading(false);
    }
  }, [seriesName, nowPlaying]);

  useEffect(() => {
    setLoading(true);
    setNextPreloaded(false);
    fetchNowPlaying(false);

    resyncTimerRef.current = setInterval(() => fetchNowPlaying(true), RESYNC_INTERVAL);
    return () => {
      if (resyncTimerRef.current) clearInterval(resyncTimerRef.current);
    };
  }, [seriesName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (nowPlaying && videoRef.current) {
      const videoUrl = getStreamUrl(nowPlaying.series, nowPlaying.video);
      const currentPath = videoRef.current.src.split("?")[0];
      const targetPath = videoUrl.split("?")[0];

      if (currentPath !== targetPath) {
        videoRef.current.src = videoUrl;
        videoRef.current.currentTime = nowPlaying.offset;
        videoRef.current.muted = isMuted;
        videoRef.current.play().catch((e) => console.log("Autoplay blocked:", e));
      }
    }
  }, [nowPlaying, isMuted, getStreamUrl]);

  useEffect(() => {
    if (nowPlaying?.next && nextVideoRef.current && !nextPreloaded) {
      nextVideoRef.current.src = getStreamUrl(nowPlaying.series, nowPlaying.next);
      nextVideoRef.current.load();
      setNextPreloaded(true);
    }
  }, [nowPlaying, nextPreloaded, getStreamUrl]);

  const handleVideoEnded = useCallback(() => {
    if (nowPlaying?.next && nowPlaying.playlist) {
      const currentIndex = nowPlaying.playlist.indexOf(nowPlaying.video);
      const nextIndex = (currentIndex + 1) % nowPlaying.playlist.length;
      const nextVideo = nowPlaying.playlist[nextIndex];

      setNowPlaying((prev) => ({
        ...prev,
        video: nextVideo,
        offset: 0,
        next: nowPlaying.playlist[(nextIndex + 1) % nowPlaying.playlist.length],
      }));
      setNextPreloaded(false);

      if (videoRef.current) {
        videoRef.current.src = getStreamUrl(nowPlaying.series, nextVideo);
        videoRef.current.currentTime = 0;
        videoRef.current.muted = isMuted;
        videoRef.current.play().catch((e) => console.log("Autoplay blocked:", e));
      }
    }
  }, [nowPlaying, isMuted, getStreamUrl]);

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const current = videoRef.current.currentTime;
    const total = videoRef.current.duration;
    setCurrentTime(current);
    setDuration(total);
    if (total > 0) setProgress((current / total) * 100);
  };

  const toggleFullscreen = useCallback(() => {
    const elem = containerRef.current;
    if (!elem) return;

    try {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        const target = elem.requestFullscreen ? elem : videoRef.current;
        if (target?.requestFullscreen) {
          target.requestFullscreen().catch(() => {
            if (videoRef.current?.webkitEnterFullscreen) videoRef.current.webkitEnterFullscreen();
          });
        } else if (target?.webkitRequestFullscreen) {
          target.webkitRequestFullscreen();
        } else if (videoRef.current?.webkitEnterFullscreen) {
          videoRef.current.webkitEnterFullscreen();
        }
      } else if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    } catch (err) {
      console.log("Fullscreen not available:", err.message);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement || !!document.webkitFullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, []);

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      const newMuted = !videoRef.current.muted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
    }
  }, []);

  const resetControlsTimeout = useCallback(() => {
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
  }, []);

  const handleScreenClick = useCallback(() => {
    if (showControls) {
      setShowControls(false);
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    } else {
      setShowControls(true);
      resetControlsTimeout();
    }
  }, [showControls, resetControlsTimeout]);

  const handleSeriesChange = (newSeries) => navigate(`/watch/${newSeries}`);
  const handleMouseMove = () => {
    setShowControls(true);
    resetControlsTimeout();
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      switch (e.key.toLowerCase()) {
        case "f":
          e.preventDefault();
          toggleFullscreen();
          break;
        case "m":
          e.preventDefault();
          toggleMute();
          break;
        case "escape":
          if (document.fullscreenElement || document.webkitFullscreenElement) {
            if (document.exitFullscreen) document.exitFullscreen();
            else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
          }
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleFullscreen, toggleMute]);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    document.title = nowPlaying ? `${seriesName.replace(/_/g, " ")} - Live Channel` : "Live Channel";
    return () => { document.title = "HappyTV"; };
  }, [seriesName, nowPlaying]);

  if (loading) {
    return (
      <div data-testid="player-loading" className="h-screen w-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="player-error" className="h-screen w-screen bg-black flex flex-col items-center justify-center p-4">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <p className="text-red-400 text-lg mb-4">{error}</p>
        <button
          data-testid="back-to-home-btn"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 px-4 py-2 bg-white text-black font-bold uppercase tracking-wider text-sm hover:bg-gray-200 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Channels
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      data-testid="player-container"
      className="h-screen w-screen bg-black relative overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      <video
        ref={videoRef}
        data-testid="video-player"
        className="absolute inset-0 w-full h-full object-contain"
        autoPlay
        muted={isMuted}
        playsInline
        onEnded={handleVideoEnded}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleTimeUpdate}
        onDoubleClick={toggleFullscreen}
      />
      <video ref={nextVideoRef} className="hidden" preload="auto" muted />
      <div className="absolute inset-0" onClick={handleScreenClick} />

      <div className={`absolute top-0 left-0 right-0 p-4 md:p-6 bg-gradient-to-b from-black/80 to-transparent transition-opacity duration-300 ${showControls ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        <div className="flex items-center justify-between">
          <button data-testid="back-btn" onClick={() => navigate("/")} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium hidden md:inline">Retour</span>
          </button>

          <Select value={seriesName} onValueChange={handleSeriesChange}>
            <SelectTrigger data-testid="series-selector" className="w-[200px] bg-black/50 border-white/20 text-white focus:ring-white">
              <SelectValue placeholder="Select channel" />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0a] border-white/20">
              {allSeries.map((s) => (
                <SelectItem key={s.name} value={s.name} data-testid={`series-option-${s.name}`} className="text-white focus:bg-white/10 focus:text-white">
                  {s.name.replace(/_/g, " ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent transition-opacity duration-300 ${showControls ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        <div className="px-4 md:px-6 mb-2">
          <div className="relative h-1 bg-white/20 cursor-not-allowed">
            <div className="absolute top-0 left-0 h-full bg-red-600 transition-all duration-150" style={{ width: `${progress}%` }} />
          </div>
          <div className="flex justify-between mt-1 text-xs text-white/50">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        <div className="px-4 md:px-6 pb-4 md:pb-6">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse-live" />
                <span className="text-red-500 text-xs font-bold uppercase tracking-wider">Live</span>
              </div>
              <h2 data-testid="now-playing-title" className="font-heading text-xl md:text-2xl font-semibold text-white uppercase tracking-tight truncate">
                {seriesName.replace(/_/g, " ")}
              </h2>
              <p data-testid="now-playing-filename" className="text-white/60 text-sm truncate flex items-center gap-2">
                {(nowPlaying?.video_info?.season || nowPlaying?.video_info?.episode) && (
                  <span className="px-1.5 py-0.5 bg-red-600 text-white text-xs font-bold flex-shrink-0">
                    {nowPlaying.video_info.season ? `S${nowPlaying.video_info.season.toString().padStart(2, "0")}` : ""}
                    {nowPlaying.video_info.episode ? `E${nowPlaying.video_info.episode.toString().padStart(2, "0")}` : ""}
                  </span>
                )}
                <span className="truncate">{nowPlaying?.video_info?.title || "Sans titre"}</span>
              </p>
              {nowPlaying?.next && (
                <p data-testid="next-up" className="flex items-center gap-2 text-white/40 text-xs mt-1">
                  <SkipForward className="w-3 h-3 flex-shrink-0" />
                  <span className="flex-shrink-0">À suivre :</span>
                  {(nowPlaying?.next_info?.season || nowPlaying?.next_info?.episode) && (
                    <span className="px-1 py-0.5 bg-white/20 text-white text-xs flex-shrink-0">
                      {nowPlaying.next_info.season ? `S${nowPlaying.next_info.season.toString().padStart(2, "0")}` : ""}
                      {nowPlaying.next_info.episode ? `E${nowPlaying.next_info.episode.toString().padStart(2, "0")}` : ""}
                    </span>
                  )}
                  <span className="truncate">{nowPlaying?.next_info?.title || "Sans titre"}</span>
                </p>
              )}
            </div>

            <div className="flex items-center gap-2 md:gap-3">
              <button
                data-testid="mute-btn"
                onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                className={`flex items-center gap-2 px-4 py-2 md:px-5 md:py-3 font-bold uppercase tracking-wider text-xs transition-colors ${isMuted ? "bg-white text-black hover:bg-gray-200" : "bg-white/10 text-white hover:bg-white/20"}`}
                aria-label={isMuted ? "Activer le son" : "Couper le son"}
              >
                {isMuted ? <><VolumeX className="w-4 h-4" /><span className="hidden md:inline">Son</span></> : <><Volume2 className="w-4 h-4" /><span className="hidden md:inline">Mute</span></>}
              </button>

              <button
                data-testid="fullscreen-btn"
                onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
                className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
                aria-label={isFullscreen ? "Quitter plein écran" : "Plein écran"}
              >
                {isFullscreen ? <Minimize className="w-5 h-5 text-white" /> : <Maximize className="w-5 h-5 text-white" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
